import { useMemo } from 'react';
import { keyBy } from 'lodash';

import ErrorReporter from '@lyearn/core/utils/ErrorReporter';
import { jsonStringify } from '@lyearn/core/utils/json';
import { useQuizContentQuery } from '@/modules/corporate/modules/quiz/views/QuizPlayer/graphql/queries/generated/quizContent';
import { CorrectOptionsFilters, ResourceStatusEnum } from '@/types/schema';

import { QuestionPerformanceFragment } from '../../graphql/fragments/generated/QuestionPerformance';
import { QuizPerformanceFragment } from '../../graphql/fragments/generated/QuizPerformance';
import { QuizPlayerContentFragment } from '../../graphql/fragments/generated/QuizPlayerContent';
import { useQuestionPerformancesQuery } from '../../graphql/queries/generated/questionPerformances';
import { useQuizPerformancesQuery } from '../../graphql/queries/generated/quizPerformances';
import getFetchCorrectAnswers from '../../utils/getFetchCorrectAnswers';
import getQuestionIds from '../../utils/getQuestionIds';

interface Params {
  userId: string;
  contentId: string;
  parentEntityId: string;
  isEmbed?: boolean;
  versionStatus?: ResourceStatusEnum;
  attemptId: string;
  previewFilter: CorrectOptionsFilters;
  isLocalizedContent?: boolean;
}

const QUERY_CONTEXT = { skipRefocus: true };

const useQuizPlayerData = ({
  contentId,
  parentEntityId,
  userId,
  isEmbed,
  versionStatus,
  attemptId,
  previewFilter,
  isLocalizedContent: isLocalizedContent_,
}: Params) => {
  const [quizContent] = useQuizContentQuery({
    variables: { contentId, isEmbed, versionStatus, attemptId },
    context: QUERY_CONTEXT,
  });
  const quiz = quizContent.data?.content as QuizPlayerContentFragment | undefined;
  const isLocalizedContent = isLocalizedContent_ ?? quiz?.isLocalizedContent;
  const [quizPerformanceData] = useQuizPerformancesQuery({
    variables: {
      filters: { entityIds: [contentId], userIds: [userId], parentEntityId, isLocalizedContent },
    },
    pause: quizContent.fetching,
    requestPolicy: 'network-only',
  });
  const quizPerformance = quizPerformanceData.data?.contentPerformances.edges?.[0]?.node as
    | QuizPerformanceFragment
    | undefined;

  const questionIds = useMemo(() => getQuestionIds(quiz), [quiz]);

  const [questionPerformancesData] = useQuestionPerformancesQuery({
    variables: {
      filters: { userIds: [userId], parentEntityId, entityIds: questionIds, isLocalizedContent },
      first: -1,
      previewFilter,
      fetchCorrectAnswers: getFetchCorrectAnswers(
        quiz?.settings?.revealAnswers,
        quizPerformance,
        quiz?.settings?.retakeAllowedCount,
      ),
    },
    pause: quizContent.fetching,
    requestPolicy: 'network-only',
  });

  const questionPerformanceById = useMemo(() => {
    const questionPerformances = (questionPerformancesData.data?.contentPerformances.edges?.map(
      ({ node }) => node,
    ) || []) as QuestionPerformanceFragment[];

    const performancesById = keyBy(questionPerformances, 'entityId');

    if (Object.values(performancesById).length !== questionPerformances.length) {
      ErrorReporter.info(
        '[Quiz] Invalid Data for Review Screen - performances contains a duplicate',
        {
          tags: { quizId: contentId, userId },
          extra: {
            quizPerformance: jsonStringify(quizPerformance),
            questionPerformances: jsonStringify(questionPerformancesData.data),
          },
        },
      );
    }

    return performancesById;
  }, [contentId, questionPerformancesData.data, quizPerformance, userId]);

  return {
    error: quizContent.error || quizPerformanceData.error || questionPerformancesData.error,
    stale: quizContent.stale || quizPerformanceData.stale || questionPerformancesData.stale,
    fetching:
      quizContent.fetching || quizPerformanceData.fetching || questionPerformancesData.fetching,
    quiz,
    quizPerformance,
    questionPerformanceById,
  };
};

export type UseQuizPlayerDataReturnType = ReturnType<typeof useQuizPlayerData>;
export default useQuizPlayerData;
